import type {SiteLocale} from "seo/siteMeta";
import {localeCopyWithEnglishFallback} from "i18n/localeFallback";

/**
 * Per-page copy for the public **Zero-Knowledge / E2EE** explainer page (`/zero-knowledge`, ui#133).
 *
 * One of the TWO files the content lives in (the other is {@link ../ZeroKnowledgeContent.tsx}). Holds
 * every locale; the page selects one at runtime via `useLocale()`. Routes, SEO wiring and the Resources
 * menu link already exist elsewhere - only the strings below and the component's dumb markup make the page.
 *
 * ## Accuracy contract
 *
 * Every claim here is grounded in the actual E2EE core and mirrors the owner-approved Privacy Policy
 * wording ({@link ../../../i18n/richText.en.tsx}). The two hosted key modes are, precisely:
 *  - **Server-escrow (default, every plan):** files encrypted on-device (AES-256-GCM); the file key is
 *    RSA-OAEP-wrapped with the server's AWS KMS public key and stored server-side, so Transferify is
 *    *technically able* to decrypt (KMS `Decrypt`). See `core/crypto/serverKeyWrap.ts`,
 *    `core/upload/EncryptedUploadClient.ts` (`keyMode !== "zeroKnowledge"`).
 *  - **Zero-Knowledge (paid):** nothing key-related is sent; the 256-bit AES key rides ONLY in the
 *    share link's `#` fragment (browsers never transmit the fragment), so the server never receives it
 *    and cannot decrypt. See `core/crypto/fragment.ts`, `core/crypto/keys.ts`.
 * Chunk size is derived PER FILE (`core/upload/cipherSizing.ts` `scaledCipherChunkSize`, ui#205): the
 * 8 MiB `CIPHER_CHUNK_SIZE` base for files that fit S3's 10,000-part cap, scaling in multiples up to
 * 128 MiB beyond it. Copy must therefore never state a fixed chunk size — say "8 MiB, larger as the
 * file grows". The optional password wraps the key with PBKDF2-SHA256 at 600k iterations
 * (`core/crypto/passwordWrap.ts`) into `#p=`, never reaching the server.
 */

/** One row of the two-mode comparison table: an aspect and how each key mode handles it. */
export interface ZeroKnowledgeComparisonRow {
	/** Row header (the aspect being compared). */
	label: string;
	/** How the default server-escrow mode handles it. */
	escrow: string;
	/** How Zero-Knowledge mode handles it. */
	zeroKnowledge: string;
}

/** The side-by-side "two key modes" comparison rendered as a real, crawlable `<table>`. */
export interface ZeroKnowledgeComparison {
	/** `<h2>` above the table. */
	heading: string;
	/** Intro paragraph under the heading. */
	intro: string;
	/** Top-left corner column header (names what the left column lists). */
	featureHeading: string;
	/** Column header for the default server-escrow mode. */
	escrowHeading: string;
	/** Column header for Zero-Knowledge mode. */
	zeroKnowledgeHeading: string;
	/** Comparison rows. */
	rows: ZeroKnowledgeComparisonRow[];
}

/** One prose section: a heading, one or more paragraphs, an optional bullet list, an optional closer. */
export interface ZeroKnowledgeSection {
	/** `<h2>` for the section. */
	heading: string;
	/** Body paragraphs - each renders as its own `<p>`. */
	paragraphs: string[];
	/** Optional `<ul>` bullets rendered after the paragraphs. */
	bullets?: string[];
	/** Optional trailing paragraph rendered after the bullets. */
	closing?: string;
}

/** Full copy for the Zero-Knowledge explainer page. */
export interface ZeroKnowledgeCopy {
	/** `<Seo>` metadata - the browser/search title and meta description for this page. */
	seo: {
		/** Page title (the site name is appended automatically by `<Seo>`). */
		title: string;
		/** Meta description (~150-160 chars for a healthy search snippet). */
		description: string;
	};
	/** The single `<h1>` at the top of the page. */
	heading: string;
	/** One-line intro paragraph directly under the heading. */
	lede: string;
	/** Framing paragraphs - what "zero-knowledge" precisely means here. */
	intro: string[];
	/** The two-mode side-by-side comparison. */
	comparison: ZeroKnowledgeComparison;
	/** The explainer sections below the comparison, in order. */
	sections: ZeroKnowledgeSection[];
	/** Closing paragraph. */
	closing: string;
}

/** EN + RO + DE + FR + IT copy, keyed by locale. `ZeroKnowledgeContent` reads `zeroKnowledgeCopy[locale]`. */
export const zeroKnowledgeCopy: Record<SiteLocale, ZeroKnowledgeCopy> = localeCopyWithEnglishFallback<ZeroKnowledgeCopy>({
	en: {
		seo: {
			title: "Zero-Knowledge encryption for file transfer",
			description:
				"How Transferify's Zero-Knowledge mode keeps files private: they are encrypted on your " +
				"device and the key never reaches our servers.",
		},
		heading: "Zero-Knowledge encryption",
		lede:
			"Every hosted transfer is encrypted on your device before it leaves. Zero-Knowledge mode goes " +
			"one step further: the key that unlocks your files never reaches our servers - so, unlike our " +
			"default, we genuinely cannot read them.",
		intro: [
			"“Zero-knowledge” is a strong claim, so it is worth being exact about what it means " +
				"here. To read your files, one thing is needed: the encryption key. Zero-knowledge means that " +
				"key never reaches us - we hold zero knowledge of it. The encrypted data we store is, " +
				"without the key, just noise on a disk.",
			"That precision cuts both ways. Every hosted transfer is encrypted on your device with " +
				"AES-256-GCM, in every mode - but only Zero-Knowledge mode keeps the key entirely off our " +
				"servers. So it is the only mode in which we truly cannot decrypt your files, and the only one " +
				"we describe that way.",
		],
		comparison: {
			heading: "The two key modes, side by side",
			intro:
				"Both modes encrypt your files on your device, at the same strength. The only difference is " +
				"what happens to the key that unlocks them.",
			featureHeading: "What we compare",
			escrowHeading: "Server-escrow (default)",
			zeroKnowledgeHeading: "Zero-Knowledge (paid)",
			rows: [
				{
					label: "Where files are encrypted",
					escrow: "On your device, before upload",
					zeroKnowledge: "On your device, before upload",
				},
				{
					label: "What we store",
					escrow: "The ciphertext plus the key, wrapped by a cloud managed encryption key",
					zeroKnowledge: "The ciphertext only - no key, ever",
				},
				{
					label: "Where the key lives",
					escrow: "In escrow on our servers, wrapped by a cloud managed key",
					zeroKnowledge: "Only in the share link’s “#” fragment",
				},
				{
					label: "Can Transferify decrypt your files?",
					escrow: "Technically yes - we can unwrap the key",
					zeroKnowledge: "No - we never receive the key",
				},
				{
					label: "If you lose the link",
					escrow: "Still recoverable - we hold the key",
					zeroKnowledge: "Gone for good - we cannot help",
				},
				{
					label: "Availability",
					escrow: "Every plan, including free",
					zeroKnowledge: "Plans that include the option",
				},
			],
		},
		sections: [
			{
				heading: "Where the key lives - and why the “#” in the link matters",
				paragraphs: [
					"A Transferify file key is a random 256-bit AES key generated in your browser. In " +
						"Zero-Knowledge mode it reaches your recipient inside the link fragment - the part " +
						"after the “#” symbol, for example transferify.eu/download/abc123#k=…",
					"That is the whole trick. By a rule every browser follows, the fragment is never sent to " +
						"the server: it stays in the browser and is used only on the page itself. So when your " +
						"recipient opens the link, the key is read locally to decrypt the download, and our " +
						"servers see the request without the “#” part. We simply never receive it.",
					"It also means the link is the key. Anyone you give the full link to can open the files, so " +
						"share it over a channel you trust. For extra safety you can add a password: the key is " +
						"then wrapped with your password (PBKDF2, 600,000 iterations) before it goes in the link " +
						"and unwrapped in your recipient’s browser - the password never reaches us either.",
				],
			},
			{
				heading: "How your files are encrypted",
				paragraphs: [
					"Files are encrypted with AES-256-GCM, a standard authenticated cipher used across the " +
						"modern web. Large files are split into chunks - 8 MiB for everyday transfers, larger as " +
						"the file grows; each chunk is sealed and authentication-tagged on its own, then streamed " +
						"straight to storage - so encryption works even on files far larger than your device’s " +
						"memory.",
					"Every chunk is bound to its exact position in its transfer. If our storage ever returned a " +
						"tampered, reordered, or swapped chunk, decryption fails loudly instead of handing your " +
						"recipient corrupted or wrong data. Files are stored in the EU, on Cloudflare R2 in a " +
						"bucket restricted to the EU jurisdiction or on AWS S3 in Frankfurt, and kept encrypted at rest.",
				],
			},
			{
				heading: "What the default mode does - and why it is honest to say so",
				paragraphs: [
					"By default, and on the free plan, we use server-escrow. Your files are still encrypted on " +
						"your device, but we keep the file key in escrow, wrapped by a cloud managed encryption " +
						"key. That means we are technically able to decrypt a hosted file - for example to " +
						"deliver it, or to comply with a lawful request - so we do not claim to be blind to it.",
					"The upside is convenience: the download link is short, carries no key, and a lost link is " +
						"still recoverable because we can unwrap the key again. It is the right default for " +
						"everyday sharing. It is not zero-knowledge, and we do not call it that.",
				],
			},
			{
				heading: "When to choose Zero-Knowledge",
				paragraphs: [
					"Turn on Zero-Knowledge mode when “trust us” is not good enough - when you " +
						"need it to be provable that the provider cannot read a file.",
				],
				bullets: [
					"Confidential documents - legal, financial, medical, HR",
					"Credentials, keys, or anything you would never send in plain text",
					"Work under a strict privacy or compliance requirement",
					"Anytime you would rather the key simply never exist on our side",
				],
				closing:
					"The trade-off is that we cannot help you recover a lost link - because we hold " +
					"nothing to recover from. Keep the link safe (and consider a password), and your files stay " +
					"private even from us.",
			},
		],
		closing:
			"Not sure which you need? Every transfer is encrypted either way - start with the default, " +
			"and switch on Zero-Knowledge for the ones that really matter.",
	},
	ro: {
		seo: {
			title: "Criptare Zero-Knowledge end-to-end",
			description:
				"Cum îți păstrează private fișierele modul Zero-Knowledge: sunt criptate pe dispozitivul " +
				"tău, iar cheia nu ajunge pe serverele noastre.",
		},
		heading: "Criptare Zero-Knowledge",
		lede:
			"Fiecare transfer găzduit este criptat pe dispozitivul tău înainte să plece. Modul Zero-Knowledge " +
			"merge un pas mai departe: cheia care îți deblochează fișierele nu ajunge niciodată pe serverele " +
			"noastre - așa că, spre deosebire de modul implicit, chiar nu le putem citi.",
		intro: [
			"„Zero-knowledge” este o afirmație puternică, așa că merită să fim exacți în privința a " +
				"ceea ce înseamnă aici. Ca să-ți citească cineva fișierele, e nevoie de un singur lucru: cheia " +
				"de criptare. Zero-knowledge înseamnă că această cheie nu ajunge niciodată la noi - nu " +
				"deținem nicio informație despre ea. Datele criptate pe care le stocăm sunt, fără cheie, doar " +
				"zgomot pe un disc.",
			"Precizia aceasta funcționează în ambele sensuri. Fiecare transfer găzduit este criptat pe " +
				"dispozitivul tău cu AES-256-GCM, în orice mod - dar numai modul Zero-Knowledge ține cheia " +
				"complet în afara serverelor noastre. Este deci singurul mod în care chiar nu îți putem decripta " +
				"fișierele și singurul pe care îl descriem astfel.",
		],
		comparison: {
			heading: "Cele două moduri de gestionare a cheii, comparate",
			intro:
				"Ambele moduri îți criptează fișierele pe dispozitiv, la fel de puternic. Singura diferență " +
				"este ce se întâmplă cu cheia care le deblochează.",
			featureHeading: "Ce comparăm",
			escrowHeading: "Cheie în custodie (implicit)",
			zeroKnowledgeHeading: "Zero-Knowledge (plătit)",
			rows: [
				{
					label: "Unde sunt criptate fișierele",
					escrow: "Pe dispozitivul tău, înainte de încărcare",
					zeroKnowledge: "Pe dispozitivul tău, înainte de încărcare",
				},
				{
					label: "Ce stocăm",
					escrow: "Datele criptate plus cheia, împachetată cu o cheie de criptare gestionată în cloud",
					zeroKnowledge: "Doar datele criptate - nicio cheie, niciodată",
				},
				{
					label: "Unde se află cheia",
					escrow: "În custodie, pe serverele noastre, împachetată cu o cheie gestionată în cloud",
					zeroKnowledge: "Doar în fragmentul „#” al linkului",
				},
				{
					label: "Poate Transferify să îți decripteze fișierele?",
					escrow: "Tehnic, da - putem desface cheia",
					zeroKnowledge: "Nu - nu primim niciodată cheia",
				},
				{
					label: "Dacă pierzi linkul",
					escrow: "Tot recuperabil - noi deținem cheia",
					zeroKnowledge: "Pierdut definitiv - nu te putem ajuta",
				},
				{
					label: "Disponibilitate",
					escrow: "Orice plan, inclusiv cel gratuit",
					zeroKnowledge: "Planurile care includ opțiunea",
				},
			],
		},
		sections: [
			{
				heading: "Unde se află cheia - și de ce contează „#” din link",
				paragraphs: [
					"O cheie de fișier Transferify este o cheie AES aleatorie de 256 de biți, generată în " +
						"browserul tău. În modul Zero-Knowledge, ea ajunge la destinatar în interiorul " +
						"fragmentului linkului - partea de după simbolul „#”, de exemplu " +
						"transferify.eu/download/abc123#k=…",
					"Aici este tot secretul. Printr-o regulă pe care o respectă orice browser, fragmentul nu " +
						"este niciodată trimis către server: rămâne în browser și este folosit doar pe pagina în " +
						"sine. Așa că, atunci când destinatarul deschide linkul, cheia este citită local pentru a " +
						"decripta descărcarea, iar serverele noastre văd cererea fără partea „#”. Pur și " +
						"simplu nu o primim niciodată.",
					"Asta înseamnă și că linkul este cheia. Oricine primește linkul complet poate deschide " +
						"fișierele, așa că trimite-l pe un canal în care ai încredere. Pentru siguranță în plus poți " +
						"adăuga o parolă: cheia este atunci împachetată cu parola ta (PBKDF2, 600.000 de iterații) " +
						"înainte să intre în link și desfăcută în browserul destinatarului - nici parola nu " +
						"ajunge la noi.",
				],
			},
			{
				heading: "Cum îți sunt criptate fișierele",
				paragraphs: [
					"Fișierele sunt criptate cu AES-256-GCM, un cifru autentificat standard, folosit peste tot " +
						"pe internetul modern. Fișierele mari sunt împărțite în bucăți - 8 MiB pentru transferurile " +
						"obișnuite și mai mari pe măsură ce fișierul crește; fiecare bucată este sigilată și " +
						"semnată separat, apoi transmisă direct către stocare - așa că criptarea funcționează " +
						"chiar și pentru fișiere mult mai mari decât memoria dispozitivului tău.",
					"Fiecare bucată este legată de poziția ei exactă în transfer. Dacă stocarea noastră ar " +
						"returna vreodată o bucată modificată, reordonată sau înlocuită, decriptarea eșuează " +
						"zgomotos, în loc să-i predea destinatarului date corupte sau greșite. Fișierele sunt " +
						"stocate în UE, pe Cloudflare R2, într-un bucket restricționat la jurisdicția UE, sau pe " +
						"AWS S3 în Frankfurt, și păstrate criptate în repaus.",
				],
			},
			{
				heading: "Ce face modul implicit - și de ce e corect s-o spunem",
				paragraphs: [
					"În mod implicit, și pe planul gratuit, folosim cheie în custodie. Fișierele tale sunt tot " +
						"criptate pe dispozitiv, dar noi păstrăm cheia fișierului în custodie, împachetată cu o " +
						"cheie de criptare gestionată în cloud. Asta înseamnă că suntem tehnic capabili să " +
						"decriptăm un fișier găzduit - de exemplu ca să-l livrăm sau ca să răspundem unei cereri " +
						"legale - așa că nu pretindem că nu îl putem vedea.",
					"Avantajul este comoditatea: linkul de descărcare este scurt, nu conține nicio cheie, iar un " +
						"link pierdut rămâne recuperabil, pentru că putem desface cheia din nou. Este alegerea " +
						"potrivită pentru partajarea de zi cu zi. Nu este zero-knowledge și nu îl numim așa.",
				],
			},
			{
				heading: "Când să alegi Zero-Knowledge",
				paragraphs: [
					"Activează modul Zero-Knowledge atunci când „ai încredere în noi” nu este " +
						"suficient - când ai nevoie să fie demonstrabil că furnizorul nu poate citi un fișier.",
				],
				bullets: [
					"Documente confidențiale - juridice, financiare, medicale, de resurse umane",
					"Parole, chei sau orice nu ai trimite vreodată în text simplu",
					"Lucru sub o cerință strictă de confidențialitate sau conformitate",
					"Oricând preferi ca acea cheie pur și simplu să nu existe la noi",
				],
				closing:
					"Compromisul este că nu te putem ajuta să recuperezi un link pierdut - pentru că nu " +
					"deținem nimic de recuperat. Păstrează linkul în siguranță (și ia în calcul o parolă), iar " +
					"fișierele tale rămân private chiar și față de noi.",
			},
		],
		closing:
			"Nu ești sigur de care ai nevoie? Fiecare transfer este criptat oricum - începe cu modul " +
			"implicit și activează Zero-Knowledge pentru fișierele care contează cu adevărat.",
	},
	de: {
		seo: {
			title: "Zero-Knowledge-Verschlüsselung für Transfers",
			description:
				"Wie der Zero-Knowledge-Modus deine Dateien privat hält: verschlüsselt auf deinem Gerät, " +
				"und der Schlüssel erreicht unsere Server nie.",
		},
		heading: "Zero-Knowledge-Verschlüsselung",
		lede:
			"Jeder gehostete Transfer wird auf deinem Gerät verschlüsselt, bevor er es verlässt. Der " +
			"Zero-Knowledge-Modus geht einen Schritt weiter: Der Schlüssel, der deine Dateien öffnet, " +
			"erreicht unsere Server nie - anders als im Standardmodus können wir sie also wirklich nicht lesen.",
		intro: [
			"„Zero-Knowledge“ ist eine starke Behauptung, also lohnt es sich, genau zu sagen, was sie hier " +
				"bedeutet. Um deine Dateien zu lesen, braucht es genau eine Sache: den Schlüssel. " +
				"Zero-Knowledge heißt, dass dieser Schlüssel uns nie erreicht - wir wissen null über ihn. Die " +
				"verschlüsselten Daten, die wir speichern, sind ohne den Schlüssel nur Rauschen auf einer Platte.",
			"Diese Genauigkeit schneidet in beide Richtungen. Jeder gehostete Transfer wird auf deinem Gerät " +
				"mit AES-256-GCM verschlüsselt, in jedem Modus - aber nur der Zero-Knowledge-Modus hält den " +
				"Schlüssel vollständig von unseren Servern fern. Er ist damit der einzige Modus, in dem wir " +
				"deine Dateien wirklich nicht entschlüsseln können, und der einzige, den wir so beschreiben.",
		],
		comparison: {
			heading: "Die zwei Schlüsselmodi nebeneinander",
			intro:
				"Beide Modi verschlüsseln deine Dateien auf deinem Gerät, gleich stark. Der einzige " +
				"Unterschied ist, was mit dem Schlüssel passiert, der sie öffnet.",
			featureHeading: "Was wir vergleichen",
			escrowHeading: "Schlüssel bei uns hinterlegt (Standard)",
			zeroKnowledgeHeading: "Zero-Knowledge (kostenpflichtig)",
			rows: [
				{
					label: "Wo Dateien verschlüsselt werden",
					escrow: "Auf deinem Gerät, vor dem Hochladen",
					zeroKnowledge: "Auf deinem Gerät, vor dem Hochladen",
				},
				{
					label: "Was wir speichern",
					escrow: "Den Geheimtext plus den Schlüssel, eingepackt von einem cloud-verwalteten Verschlüsselungsschlüssel",
					zeroKnowledge: "Nur den Geheimtext - nie einen Schlüssel",
				},
				{
					label: "Wo der Schlüssel liegt",
					escrow: "Hinterlegt auf unseren Servern, von einem cloud-verwalteten Schlüssel eingepackt",
					zeroKnowledge: "Nur im „#“-Fragment des Freigabelinks",
				},
				{
					label: "Kann Transferify deine Dateien entschlüsseln?",
					escrow: "Technisch ja - wir können den Schlüssel auspacken",
					zeroKnowledge: "Nein - wir bekommen den Schlüssel nie",
				},
				{
					label: "Wenn du den Link verlierst",
					escrow: "Weiterhin wiederherstellbar - wir haben den Schlüssel",
					zeroKnowledge: "Endgültig weg - wir können nicht helfen",
				},
				{
					label: "Verfügbarkeit",
					escrow: "In jedem Plan, auch im kostenlosen",
					zeroKnowledge: "In den Plänen, die die Option enthalten",
				},
			],
		},
		sections: [
			{
				heading: "Wo der Schlüssel liegt - und warum das „#“ im Link zählt",
				paragraphs: [
					"Ein Transferify-Dateischlüssel ist ein zufälliger 256-Bit-AES-Schlüssel, der in deinem " +
						"Browser erzeugt wird. Im Zero-Knowledge-Modus erreicht er deinen Empfänger im " +
						"Link-Fragment - dem Teil nach dem Zeichen „#“, zum Beispiel " +
						"transferify.eu/download/abc123#k=…",
					"Das ist der ganze Kniff. Nach einer Regel, der jeder Browser folgt, wird das Fragment nie an " +
						"den Server geschickt: Es bleibt im Browser und wird nur auf der Seite selbst benutzt. Wenn " +
						"dein Empfänger den Link öffnet, wird der Schlüssel also lokal gelesen, um den Download zu " +
						"entschlüsseln, und unsere Server sehen die Anfrage ohne den „#“-Teil. Wir bekommen ihn " +
						"schlicht nie.",
					"Das heißt auch: Der Link IST der Schlüssel. Wer den vollständigen Link von dir bekommt, kann " +
						"die Dateien öffnen, also teile ihn über einen Kanal, dem du vertraust. Für zusätzliche " +
						"Sicherheit kannst du ein Passwort setzen: Der Schlüssel wird dann mit deinem Passwort " +
						"eingepackt (PBKDF2, 600.000 Durchläufe), bevor er in den Link geht, und im Browser deines " +
						"Empfängers wieder ausgepackt - auch das Passwort erreicht uns nie.",
				],
			},
			{
				heading: "Wie deine Dateien verschlüsselt werden",
				paragraphs: [
					"Dateien werden mit AES-256-GCM verschlüsselt, einem verbreiteten authentifizierten Verfahren " +
						"des modernen Webs. Große Dateien werden in Stücke geteilt - 8 MiB für alltägliche Transfers, " +
						"größer, je größer die Datei wird; jedes Stück wird einzeln versiegelt und mit einem " +
						"Authentifizierungs-Tag versehen, dann direkt in den Speicher übertragen - so funktioniert " +
						"die Verschlüsselung auch bei Dateien, die weit größer sind als der Arbeitsspeicher deines " +
						"Geräts.",
					"Jedes Stück ist an seine genaue Position in seinem Transfer gebunden. Sollte unser Speicher je " +
						"ein verändertes, umgestelltes oder vertauschtes Stück zurückgeben, scheitert die " +
						"Entschlüsselung laut, statt deinem Empfänger beschädigte oder falsche Daten zu geben. " +
						"Dateien werden in der EU gespeichert, bei Cloudflare R2 in einem auf die EU-Jurisdiktion " +
						"beschränkten Speicher oder bei AWS S3 in Frankfurt, und bleiben dort verschlüsselt.",
				],
			},
			{
				heading: "Was der Standardmodus tut - und warum es ehrlich ist, das zu sagen",
				paragraphs: [
					"Standardmäßig und im kostenlosen Plan hinterlegen wir den Schlüssel bei uns. Deine Dateien " +
						"werden weiterhin auf deinem Gerät verschlüsselt, aber wir verwahren den Dateischlüssel, " +
						"eingepackt von einem cloud-verwalteten Verschlüsselungsschlüssel. Das heißt, wir sind " +
						"technisch in der Lage, eine gehostete Datei zu entschlüsseln - etwa um sie auszuliefern " +
						"oder um einer rechtmäßigen Anordnung nachzukommen - also behaupten wir nicht, blind " +
						"dafür zu sein.",
					"Der Vorteil ist Bequemlichkeit: Der Download-Link ist kurz, trägt keinen Schlüssel, und ein " +
						"verlorener Link bleibt wiederherstellbar, weil wir den Schlüssel erneut auspacken können. Für " +
						"das alltägliche Teilen ist das der richtige Standard. Zero-Knowledge ist es nicht, und so " +
						"nennen wir es auch nicht.",
				],
			},
			{
				heading: "Wann du Zero-Knowledge wählen solltest",
				paragraphs: [
					"Schalte den Zero-Knowledge-Modus ein, wenn „vertrau uns“ nicht gut genug ist - wenn " +
						"nachweisbar sein muss, dass der Anbieter eine Datei nicht lesen kann.",
				],
				bullets: [
					"Vertrauliche Dokumente - Recht, Finanzen, Medizin, Personal",
					"Zugangsdaten, Schlüssel oder alles, was du nie im Klartext senden würdest",
					"Arbeit unter einer strengen Datenschutz- oder Compliance-Vorgabe",
					"Immer dann, wenn dir lieber ist, dass der Schlüssel bei uns gar nicht erst existiert",
				],
				closing:
					"Der Preis dafür: Bei einem verlorenen Link können wir dir nicht helfen, weil wir nichts " +
					"haben, woraus wir ihn wiederherstellen könnten. Halte den Link sicher (und überleg dir ein " +
					"Passwort), dann bleiben deine Dateien sogar vor uns privat.",
			},
		],
		closing:
			"Nicht sicher, was du brauchst? Verschlüsselt ist jeder Transfer so oder so - fang mit dem " +
			"Standard an und schalte Zero-Knowledge für die Transfers ein, auf die es wirklich ankommt.",
	},
	fr: {
		seo: {
			title: "Chiffrement Zéro connaissance des fichiers",
			description:
				"Comment le mode Zéro connaissance de Transferify protège vos fichiers\u00a0: ils sont chiffrés sur votre " +
				"appareil et la clé n’atteint jamais nos serveurs.",
		},
		heading: "Chiffrement Zéro connaissance",
		lede:
			"Chaque transfert hébergé est chiffré sur votre appareil avant de partir. Le mode Zéro connaissance " +
			"va un cran plus loin\u00a0: la clé qui déverrouille vos fichiers n’atteint jamais nos serveurs. " +
			"Contrairement au mode par défaut, nous sommes donc réellement incapables de les lire.",
		intro: [
			"«\u00a0Zéro connaissance\u00a0» est une affirmation forte, il faut donc être précis sur ce qu’elle signifie ici. " +
				"Pour lire vos fichiers, une seule chose est nécessaire\u00a0: la clé de chiffrement. Zéro connaissance " +
				"signifie que cette clé ne nous parvient jamais\u00a0: nous n’en avons aucune connaissance. Sans la clé, " +
				"les données chiffrées que nous stockons ne sont que du bruit sur un disque.",
			"Cette précision vaut dans les deux sens. Chaque transfert hébergé est chiffré sur votre appareil avec " +
				"AES-256-GCM, quel que soit le mode, mais seul le mode Zéro connaissance garde la clé entièrement " +
				"hors de nos serveurs. C’est donc le seul mode dans lequel nous ne pouvons réellement pas déchiffrer " +
				"vos fichiers, et le seul que nous décrivons ainsi.",
		],
		comparison: {
			heading: "Les deux modes de clé, côte à côte",
			intro:
				"Les deux modes chiffrent vos fichiers sur votre appareil, avec la même robustesse. La seule " +
				"différence tient au sort de la clé qui les déverrouille.",
			featureHeading: "Ce que nous comparons",
			escrowHeading: "Séquestre côté serveur (par défaut)",
			zeroKnowledgeHeading: "Zéro connaissance (payant)",
			rows: [
				{
					label: "Où les fichiers sont chiffrés",
					escrow: "Sur votre appareil, avant l’envoi",
					zeroKnowledge: "Sur votre appareil, avant l’envoi",
				},
				{
					label: "Ce que nous stockons",
					escrow: "Les données chiffrées et la clé, protégée par une clé de chiffrement gérée dans le cloud",
					zeroKnowledge: "Uniquement les données chiffrées\u00a0: jamais de clé",
				},
				{
					label: "Où se trouve la clé",
					escrow: "En séquestre sur nos serveurs, protégée par une clé gérée dans le cloud",
					zeroKnowledge: "Uniquement dans le fragment «\u00a0#\u00a0» du lien de partage",
				},
				{
					label: "Transferify peut-il déchiffrer vos fichiers\u00a0?",
					escrow: "Techniquement oui\u00a0: nous pouvons déballer la clé",
					zeroKnowledge: "Non\u00a0: nous ne recevons jamais la clé",
				},
				{
					label: "Si vous perdez le lien",
					escrow: "Toujours récupérable\u00a0: nous détenons la clé",
					zeroKnowledge: "Perdu pour de bon\u00a0: nous ne pouvons rien faire",
				},
				{
					label: "Disponibilité",
					escrow: "Tous les plans, y compris gratuit",
					zeroKnowledge: "Plans qui incluent l’option",
				},
			],
		},
		sections: [
			{
				heading: "Où se trouve la clé, et pourquoi le «\u00a0#\u00a0» du lien compte",
				paragraphs: [
					"Une clé de fichier Transferify est une clé AES de 256 bits aléatoire, générée dans votre navigateur. " +
						"En mode Zéro connaissance, elle parvient à votre destinataire dans le fragment du lien, la " +
						"partie située après le symbole «\u00a0#\u00a0», par exemple transferify.eu/download/abc123#k=…",
					"Là est toute l’astuce. Selon une règle que tous les navigateurs respectent, le fragment n’est jamais " +
						"envoyé au serveur\u00a0: il reste dans le navigateur et ne sert que sur la page elle-même. Quand votre " +
						"destinataire ouvre le lien, la clé est donc lue localement pour déchiffrer le téléchargement, et " +
						"nos serveurs voient la requête sans la partie «\u00a0#\u00a0». Nous ne la recevons tout simplement jamais.",
					"Cela signifie aussi que le lien est la clé. Toute personne à qui vous donnez le lien complet peut " +
						"ouvrir les fichiers\u00a0: partagez-le par un canal de confiance. Pour plus de sécurité, vous pouvez " +
						"ajouter un mot de passe\u00a0: la clé est alors protégée par votre mot de passe (PBKDF2, 600\u202f000 " +
						"itérations) avant d’entrer dans le lien, puis déverrouillée dans le navigateur de votre " +
						"destinataire. Le mot de passe ne nous parvient pas non plus.",
				],
			},
			{
				heading: "Comment vos fichiers sont chiffrés",
				paragraphs: [
					"Les fichiers sont chiffrés avec AES-256-GCM, un chiffrement authentifié standard utilisé sur tout le " +
						"web moderne. Les gros fichiers sont découpés en blocs (8\u00a0Mio pour les transferts courants, " +
						"davantage à mesure que le fichier grandit)\u00a0; chaque bloc est scellé et authentifié séparément, " +
						"puis envoyé en flux directement vers le stockage, si bien que le chiffrement fonctionne même sur " +
						"des fichiers bien plus gros que la mémoire de votre appareil.",
					"Chaque bloc est lié à sa position exacte dans son transfert. Si notre stockage renvoyait un bloc " +
						"altéré, réordonné ou échangé, le déchiffrement échouerait bruyamment au lieu de remettre à votre " +
						"destinataire des données corrompues ou erronées. Les fichiers sont stockés dans l’UE, sur " +
						"Cloudflare R2 dans un bucket limité à la juridiction de l’UE ou sur AWS S3 à Francfort, et " +
						"restent chiffrés au repos.",
				],
			},
			{
				heading: "Ce que fait le mode par défaut, et pourquoi il est honnête de le dire",
				paragraphs: [
					"Par défaut, et sur le plan gratuit, nous utilisons le séquestre côté serveur. Vos fichiers sont " +
						"toujours chiffrés sur votre appareil, mais nous gardons la clé du fichier en séquestre, protégée " +
						"par une clé de chiffrement gérée dans le cloud. Nous sommes donc techniquement en mesure de " +
						"déchiffrer un fichier hébergé, par exemple pour le livrer ou pour répondre à une demande légale\u00a0: " +
						"nous ne prétendons pas y être aveugles.",
					"L’avantage est la commodité\u00a0: le lien de téléchargement est court, ne contient aucune clé, et un lien " +
						"perdu reste récupérable puisque nous pouvons déballer de nouveau la clé. C’est le bon choix par " +
						"défaut pour le partage courant. Ce n’est pas du zéro connaissance, et nous ne l’appelons pas ainsi.",
				],
			},
			{
				heading: "Quand choisir Zéro connaissance",
				paragraphs: [
					"Activez le mode Zéro connaissance quand «\u00a0faites-nous confiance\u00a0» ne suffit pas, quand il faut " +
						"pouvoir prouver que le fournisseur ne peut pas lire un fichier.",
				],
				bullets: [
					"Documents confidentiels\u00a0: juridiques, financiers, médicaux, RH",
					"Identifiants, clés, ou tout ce que vous n’enverriez jamais en clair",
					"Travail soumis à une exigence stricte de confidentialité ou de conformité",
					"Chaque fois que vous préférez que la clé n’existe tout simplement jamais de notre côté",
				],
				closing:
					"La contrepartie\u00a0: nous ne pouvons pas vous aider à retrouver un lien perdu, car nous ne détenons " +
					"rien à partir de quoi le récupérer. Gardez le lien en lieu sûr (et pensez à un mot de passe), et " +
					"vos fichiers restent privés, même pour nous.",
			},
		],
		closing:
			"Vous ne savez pas lequel choisir\u00a0? Chaque transfert est chiffré dans les deux cas\u00a0: commencez par le " +
			"mode par défaut, et activez Zéro connaissance pour ceux qui comptent vraiment.",
	},
	it: {
		seo: {
			title: "Crittografia Zero-Knowledge per i file",
			description:
				"Come la modalità Zero-Knowledge di Transferify mantiene privati i file: cifrati sul dispositivo, " +
				"con una chiave che non raggiunge mai i nostri server.",
		},
		heading: "Crittografia Zero-Knowledge",
		lede:
			"Ogni trasferimento ospitato viene cifrato sul dispositivo prima di partire. La modalità Zero-Knowledge " +
			"fa un passo in più: la chiave che sblocca i file non raggiunge mai i nostri server, quindi, a " +
			"differenza della modalità predefinita, non possiamo davvero leggerli.",
		intro: [
			"«Zero-knowledge» è un’affermazione forte, quindi conviene essere precisi su ciò che significa qui. " +
				"Per leggere i file serve una sola cosa: la chiave di cifratura. Zero-knowledge significa che " +
				"quella chiave non ci raggiunge mai: non ne sappiamo nulla. I dati cifrati che conserviamo, " +
				"senza la chiave, sono soltanto rumore su un disco.",
			"Questa precisione vale in entrambe le direzioni. Ogni trasferimento ospitato viene cifrato sul " +
				"dispositivo con AES-256-GCM, in qualsiasi modalità, ma solo la modalità Zero-Knowledge tiene la " +
				"chiave interamente fuori dai nostri server. È quindi l’unica modalità in cui non possiamo davvero " +
				"decifrare i file, e l’unica che descriviamo così.",
		],
		comparison: {
			heading: "Le due modalità per la chiave, affiancate",
			intro:
				"Entrambe le modalità cifrano i file sul dispositivo, con la stessa robustezza. L’unica differenza " +
				"sta in ciò che accade alla chiave che li sblocca.",
			featureHeading: "Cosa confrontiamo",
			escrowHeading: "Chiave in custodia sul server (predefinita)",
			zeroKnowledgeHeading: "Zero-Knowledge (a pagamento)",
			rows: [
				{
					label: "Dove vengono cifrati i file",
					escrow: "Sul dispositivo, prima del caricamento",
					zeroKnowledge: "Sul dispositivo, prima del caricamento",
				},
				{
					label: "Cosa conserviamo",
					escrow: "I dati cifrati più la chiave, protetta da una chiave di cifratura gestita nel cloud",
					zeroKnowledge: "Solo i dati cifrati: nessuna chiave, mai",
				},
				{
					label: "Dove si trova la chiave",
					escrow: "In custodia sui nostri server, protetta da una chiave gestita nel cloud",
					zeroKnowledge: "Solo nel frammento «#» del link di condivisione",
				},
				{
					label: "Transferify può decifrare i file?",
					escrow: "Tecnicamente sì: possiamo sbloccare la chiave",
					zeroKnowledge: "No: non riceviamo mai la chiave",
				},
				{
					label: "In caso di smarrimento del link",
					escrow: "Ancora recuperabile: la chiave è in nostro possesso",
					zeroKnowledge: "Perso per sempre: non possiamo fare nulla",
				},
				{
					label: "Disponibilità",
					escrow: "Tutti i piani, compreso quello gratuito",
					zeroKnowledge: "I piani che includono l’opzione",
				},
			],
		},
		sections: [
			{
				heading: "Dove si trova la chiave, e perché conta il «#» nel link",
				paragraphs: [
					"Una chiave di file Transferify è una chiave AES casuale a 256 bit, generata nel browser. In " +
						"modalità Zero-Knowledge raggiunge il destinatario all’interno del frammento del link, la " +
						"parte che segue il simbolo «#», per esempio transferify.eu/download/abc123#k=…",
					"Qui sta tutto il trucco. Secondo una regola che ogni browser rispetta, il frammento non viene " +
						"mai inviato al server: resta nel browser e viene usato solo sulla pagina stessa. Quando il " +
						"destinatario apre il link, quindi, la chiave viene letta in locale per decifrare il download, " +
						"e i nostri server vedono la richiesta senza la parte «#». Semplicemente non la riceviamo mai.",
					"Questo significa anche che il link è la chiave. Chiunque riceva il link completo può aprire i " +
						"file, quindi va condiviso su un canale di fiducia. Per una sicurezza in più si può aggiungere " +
						"una password: la chiave viene allora protetta con la password (PBKDF2, 600.000 iterazioni) " +
						"prima di entrare nel link e sbloccata nel browser del destinatario; neanche la password " +
						"arriva mai a noi.",
				],
			},
			{
				heading: "Come vengono cifrati i file",
				paragraphs: [
					"I file vengono cifrati con AES-256-GCM, un cifrario autenticato standard usato in tutto il web " +
						"moderno. I file di grandi dimensioni sono suddivisi in blocchi: 8\u00a0MiB per i trasferimenti di " +
						"uso comune, più grandi man mano che il file cresce. Ogni blocco viene sigillato e dotato di un " +
						"proprio tag di autenticazione, poi trasmesso in streaming direttamente all’archiviazione, perciò " +
						"la cifratura funziona anche con file molto più grandi della memoria del dispositivo.",
					"Ogni blocco è legato alla sua posizione esatta nel trasferimento. Se la nostra archiviazione " +
						"restituisse mai un blocco manomesso, riordinato o scambiato, la decifratura fallirebbe in modo " +
						"evidente, invece di consegnare al destinatario dati danneggiati o errati. I file sono conservati " +
						"nell’UE, su Cloudflare R2 in un bucket limitato alla giurisdizione UE oppure su AWS S3 a " +
						"Francoforte, e restano cifrati a riposo.",
				],
			},
			{
				heading: "Cosa fa la modalità predefinita, e perché è corretto dirlo",
				paragraphs: [
					"Per impostazione predefinita, e nel piano gratuito, usiamo la custodia della chiave sul server. " +
						"I file vengono comunque cifrati sul dispositivo, ma noi conserviamo in custodia la chiave del " +
						"file, protetta da una chiave di cifratura gestita nel cloud. Ciò significa che siamo " +
						"tecnicamente in grado di decifrare un file ospitato, per esempio per consegnarlo o per " +
						"ottemperare a una richiesta legittima, e quindi non sosteniamo di non poterlo vedere.",
					"Il vantaggio è la comodità: il link di download è breve, non contiene alcuna chiave e un link " +
						"smarrito resta recuperabile, perché possiamo sbloccare di nuovo la chiave. È la scelta giusta " +
						"per la condivisione di tutti i giorni. Non è zero-knowledge, e non lo chiamiamo così.",
				],
			},
			{
				heading: "Quando scegliere Zero-Knowledge",
				paragraphs: [
					"Conviene attivare la modalità Zero-Knowledge quando «fidarsi di noi» non è sufficiente, " +
						"quando serve poter dimostrare che il fornitore non può leggere un file.",
				],
				bullets: [
					"Documenti riservati: legali, finanziari, medici, delle risorse umane",
					"Credenziali, chiavi o tutto ciò che non si invierebbe mai in chiaro",
					"Lavoro soggetto a requisiti rigorosi di privacy o conformità",
					"Ogni volta che si preferisce che la chiave semplicemente non esista dalla nostra parte",
				],
				closing:
					"Il compromesso è che non possiamo aiutare a recuperare un link smarrito, perché non conserviamo " +
					"nulla da cui recuperarlo. Se si custodisce il link con cura (magari con una password), i file " +
					"restano privati anche per noi.",
			},
		],
		closing:
			"Non sa quale scegliere? Ogni trasferimento è comunque cifrato: parta dalla modalità predefinita e " +
			"attivi Zero-Knowledge per quelli che contano davvero.",
	},
});
